import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { App } from './App.js';
import { createAppQueryClient, queryPersister } from './lib/queryClient.js';
import { registerServiceWorkerAutoReload } from './lib/registerServiceWorkerAutoReload.js';
import { recoverSessionOnce } from './lib/sessionRecovery.js';
import { ThemeProvider } from './lib/theme.js';
import './styles.css';

// Runs before render so the listener is attached the moment the page
// starts up — if the SW updates a fraction of a second after launch
// (a common case on iOS PWA resume), the reload still fires.
registerServiceWorkerAutoReload();

// A lazy route chunk that fails to load (expired Access session, or a
// stale deploy whose hashed chunks are gone) surfaces as Vite's
// preload error. Reload instead of showing a broken page — the
// navigation re-auths and picks up fresh assets in one move.
window.addEventListener('vite:preloadError', (e) => {
  e.preventDefault();
  recoverSessionOnce();
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element missing');

const queryClient = createAppQueryClient();

createRoot(rootEl).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: queryPersister,
        maxAge: 1000 * 60 * 60 * 24 * 7,
        buster: 'v1',
      }}
      onSuccess={() => {
        // Once the cache and any paused mutations are rehydrated,
        // resume mutations that were queued while offline.
        void queryClient.resumePausedMutations();
      }}
    >
      <BrowserRouter>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </BrowserRouter>
    </PersistQueryClientProvider>
  </StrictMode>,
);
